import React, { useState, useEffect } from 'react'
import i18n from 'live/services/i18n'
import WebSettingsCard from 'live/components/ui/admin/web-settings-card'
import FormGroup from 'live/components/forms/ui/form-group'
import globalNotification from 'live/services/global-notification'
import Select from 'live/components/forms/ui/select'
import styled from 'styled-components'
import ActionToggle from 'live/components/ui/action-toggle'
import api from '../api'

const PurgeSettings = (): JSX.Element => {
    const [editMode, setEditMode] = useState(false)
    const [saved, setSaved] = useState(false)
    const [config, setConfig] = useState<PurgeConfig>({
        maxDuration: 1,
        maxDurationUnit: 'HOURS',
        scheduled: false,
        cron: '0 0 * * *'
    })
    const [tempConfig, setTempConfig] = useState<PurgeConfig>(null)

    useEffect(() => {
        api.getPurgeSettings()
            .then(resp => resp.json())
            .then(data => {
                setConfig(data)
            })
            .catch(error => {
                globalNotification.error(i18n('Failed to get the purge configuration', error))
            })
    }, [])

    const onSave = (): void => {
        api.savePurgeSettings(tempConfig).then(resp => {
            if (resp.ok) {
                setEditMode(false)
                setSaved(true)
                setConfig(tempConfig)
                globalNotification.success(i18n('Settings successfully saved'))
            } else {
                globalNotification.error(i18n('Failed to save settings'))
            }
        })
    }

    const maxDuration = editMode ? tempConfig?.maxDuration : config?.maxDuration
    const isMaxDurationInvalid = maxDuration <= 0 || typeof maxDuration !== 'number' || Number.isNaN(maxDuration)

    const hoursString = i18n('hourString', { maxDuration })
    const minutesString = i18n('minuteString', { maxDuration })
    const daysString = i18n('dayString', { maxDuration })

    return (
        <WebSettingsCard
            name="plugin-purge"
            className="live-feature"
            open={editMode}
            saved={saved}
            save={onSave}
            savedMessage={i18n('Settings successfully saved')}
            toggle={(_name, status) => {
                setTempConfig(config)
                setEditMode(status === 'open')
                setSaved(status !== 'open')
            }}
            disableSave={isMaxDurationInvalid}
        >
            <h2>{i18n('Configuration')}</h2>
            <div className="form-group">
                <label>{i18n('Maximum duration')}</label>
                <MaxDuration>
                    <div>
                        <FormGroup
                            type="text"
                            className="form-group--small-input"
                            value={editMode ? tempConfig.maxDuration : config.maxDuration}
                            validationState={isMaxDurationInvalid ? 'error' : undefined}
                            help={
                                isMaxDurationInvalid && (
                                    <span className="text-danger">{i18n('Value must be greater than 0')}</span>
                                )
                            }
                            hideInput={!editMode}
                            onChange={({ target }) => {
                                setTempConfig(prevConfig => {
                                    return { ...prevConfig, maxDuration: Number(target.value) }
                                })
                            }}
                        />
                    </div>
                    <div>
                        <Select
                            hideInput={!editMode}
                            disabled={false}
                            value={editMode ? tempConfig.maxDurationUnit : config.maxDurationUnit}
                            options={[
                                { value: 'MINUTES', label: minutesString },
                                { value: 'HOURS', label: hoursString },
                                { value: 'DAYS', label: daysString }
                            ]}
                            onChange={(value: TimeUnit): void =>
                                setTempConfig(prevConfig => {
                                    return { ...prevConfig, maxDurationUnit: value }
                                })
                            }
                            className="Select-small"
                            clearable={false}
                            searchable={false}
                        />
                    </div>
                </MaxDuration>
            </div>
            <div className="form-group">
                <label>{i18n('Enable scheduled rules')}</label>
                <div className="flex">
                    <div className="flex-1">
                        <ActionToggle
                            onChange={() =>
                                setTempConfig(prevConfig => {
                                    return { ...prevConfig, scheduled: !prevConfig.scheduled }
                                })
                            }
                            elLeft={<span>on</span>}
                            elRight={<span>off</span>}
                            active={editMode ? tempConfig.scheduled : config.scheduled}
                            disabled={!editMode}
                        />
                    </div>
                    <div className="flex-1">
                        <div className="flex">
                            <span className="help-block">
                                <i className="fa fa-info-circle" />
                            </span>
                            <span className="help-block">
                                <span>
                                    {i18n(
                                        'This control enables/disables the execution of scheduled purge rules following the configured Cron'
                                    )}
                                    .
                                </span>
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <div className="form-group">
                <label>{i18n('Cron')}</label>
                <FormGroup
                    type="text"
                    value={editMode ? tempConfig.cron : config.cron}
                    hideInput={!editMode}
                    onChange={({ target }) => {
                        setTempConfig(prevConfig => {
                            return { ...prevConfig, cron: target.value }
                        })
                    }}
                />
            </div>
        </WebSettingsCard>
    )
}

export default {
    name: i18n('Configuration'),
    key: 'plugin-purge',
    section: i18n('Purge Settings'),
    configComponent: PurgeSettings
}

const MaxDuration = styled.div`
    display: flex;
    > div {
        margin: 1px;

        &:nth-child(1) {
            overflow: visible;

            .help-block {
                width: 0px;
                white-space: nowrap;
            }
        }

        &:nth-child(2) {
            flex: 1;
        }
    }
`
